import React, { useEffect } from 'react';
import { css } from '@emotion/react';
import { PhoneHoursScheduleRule } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Text,
  CheckboxField,
  TimeRangeField,
  DropdownField,
  useControlledField,
  Button,
  PopoverMenu,
  PopoverMenuItem,
  usePopoverMenu,
  useModalControl,
  Modal,
  useTooltip,
} from '@frontend/design-system';
import { EditBreakModal } from './edit-break-modal';
import { NewBreakModal } from './new-break-modal';
import { DaysOfWeek, usePhoneHoursForm } from './use-phone-hours-form';

export type BreakRules = {
  [id: string]: PhoneHoursScheduleRule;
};

export type BreakSchedulesByDay = Record<
  DaysOfWeek,
  { startTime: string; endTime: string; breakName: string; id: string }[]
>;

interface PhoneHoursFormProps {
  openSchedulesForm: ReturnType<typeof usePhoneHoursForm>['formProps']['openSchedulesForm'];
  breakSchedulesByDay: BreakSchedulesByDay;
  routingActionsEnabled?: boolean;
  availableBreakNames: string[];
  onBreakTimeChange: (props: { day: DaysOfWeek; startTime: string; endTime: string; id: string }) => void;
  onBreakChange: (props: { day: DaysOfWeek; breakName: string; id: string; isNew?: boolean }) => void;
  onBreakRemove: (props: { day: DaysOfWeek; id: string }) => void;
  onBreakAdd: (day: DaysOfWeek) => void;
  onEditBreak: (id: string, newName: string) => void;
  onDeleteBreak: (id: string) => void;
  setHasAnyBreakConflict: (hasConflict: boolean) => void;
}

const TooltipWrapper: React.FC<{
  tooltipText: string;
  children: React.ReactNode;
  tooltipDisabled?: boolean;
}> = ({ tooltipText, children, tooltipDisabled = false }) => {
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();

  return (
    <div {...triggerProps}>
      {children}
      {!tooltipDisabled && <Tooltip {...tooltipProps}>{tooltipText}</Tooltip>}
    </div>
  );
};

export const PhoneHoursForm: React.FC<PhoneHoursFormProps> = ({
  openSchedulesForm,
  breakSchedulesByDay,
  routingActionsEnabled = false,
  availableBreakNames,
  onBreakTimeChange,
  onBreakChange,
  onBreakRemove,
  onBreakAdd,
  onEditBreak,
  setHasAnyBreakConflict,
}) => {
  const { t } = useTranslation('phone');
  const labels = [t('Day'), t('Start Time'), t('End Time'), t('Break')];

  const scheduleValues = openSchedulesForm.values;
  const scheduleGetFieldProps = openSchedulesForm.getFieldProps;
  const orderedDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

  // Calculate if there are any conflicts across all days
  const hasAnyConflict = Object.values(breakSchedulesByDay).some((dayBreaks) =>
    dayBreaks.some((breakItem, index, allBreaks) =>
      allBreaks.some((otherBreak, otherIndex) => {
        if (index === otherIndex) return false; // Skip the current break
        const [startA, endA] = [breakItem.startTime, breakItem.endTime];
        const [startB, endB] = [otherBreak.startTime, otherBreak.endTime];
        return (
          (startA < endB && endA > startB) || // Overlapping intervals
          (startB < endA && endB > startA)
        );
      })
    )
  );

  // Update the global conflict state
  useEffect(() => {
    setHasAnyBreakConflict(hasAnyConflict);
  }, [hasAnyConflict, setHasAnyBreakConflict]);

  return (
    <div style={{ display: 'grid' }}>
      {/* Header Row */}
      <div
        style={{
          padding: theme.spacing(2),
          display: 'grid',
          gridTemplateColumns: `${theme.spacing(2.5)} ${theme.spacing(27)} ${theme.spacing(20)} ${theme.spacing(
            20
          )} ${theme.spacing(8.5)}`,
          alignItems: 'center',
          borderBottom: `1px solid ${theme.colors.neutral20}`,
        }}
      >
        <div></div>
        {labels.map((label) => (
          <Text key={label} size='medium' color='subdued'>
            {label}
          </Text>
        ))}
      </div>

      {/* Days of the Week Rows */}
      {orderedDays.map((day) => (
        <>
          <div
            style={{
              padding: theme.spacing(2, 0),
              display: 'grid',
              gridTemplateColumns: `${theme.spacing(4.5)} ${theme.spacing(27)} ${theme.spacing(40.5)} ${theme.spacing(
                8.5
              )}`,
              alignItems: 'center',
            }}
            key={day}
          >
            {/* Checkbox */}
            <div>
              <CheckboxField {...scheduleGetFieldProps(`selected-${day}`)} label='' hideErrorText />
            </div>

            {/* Icon and Label */}
            <div
              style={{
                display: 'flex',
                gap: theme.spacing(1),
                alignItems: 'center',
              }}
            >
              <TooltipWrapper tooltipText={!!scheduleValues[`selected-${day}`] ? t('Open') : t('Closed')}>
                <div
                  style={{
                    backgroundColor: !!scheduleValues[`selected-${day}`]
                      ? theme.colors.warning5
                      : theme.colors.neutral10,
                    width: theme.spacing(4),
                    height: theme.spacing(3),
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '4px',
                  }}
                >
                  {!!scheduleValues[`selected-${day}`] ? <Icon name='sun-small' /> : <Icon name='moon-small' />}
                </div>
              </TooltipWrapper>
              <Text>{day}</Text>
            </div>

            {/* TimeRangeField */}
            {scheduleValues[`selected-${day}`] ? (
              <TimeRangeField
                {...scheduleGetFieldProps(`range-${day}`)}
                label={day}
                interval={15}
                hideErrorText
                joiningText='to'
              />
            ) : (
              <Text
                css={{
                  color: theme.colors.neutral20,
                  backgroundColor: theme.colors.neutral5,
                  padding: theme.spacing(1),
                  margin: 0,
                  width: theme.spacing(36.5),
                  borderRadius: theme.spacing(0.5),
                }}
              >
                {t('Closed')}
              </Text>
            )}

            {/* Add Break Icon */}
            <TooltipWrapper
              tooltipDisabled={!!Object.values(availableBreakNames).length || routingActionsEnabled}
              tooltipText={t(
                'No existing breaks available to add. New breaks are created in the Call Route experience.'
              )}
            >
              <Button
                name='add-break'
                iconName='add-small'
                variant='secondary'
                disabled={!Object.values(availableBreakNames).length && !routingActionsEnabled}
                css={css`
                  width: 40px;
                `}
                onClick={() => {
                  onBreakAdd(day);
                }}
              />
            </TooltipWrapper>
          </div>

          {/* Break Rows */}
          {Object.values(breakSchedulesByDay[day] ?? [])?.map((breakItem, index, allBreaks) => {
            // Check for conflicts with other breaks on the same day
            const hasConflict = allBreaks.some((otherBreak, otherIndex) => {
              if (index === otherIndex) return false; // Skip the current break
              const [startA, endA] = [breakItem.startTime, breakItem.endTime];
              const [startB, endB] = [otherBreak.startTime, otherBreak.endTime];
              return (
                (startA < endB && endA > startB) || // Overlapping intervals
                (startB < endA && endB > startA)
              );
            });

            return (
              <div
                key={`${day}-${index}`}
                css={css`
                  display: grid;
                  grid-template-columns: ${theme.spacing(4.5)} ${theme.spacing(27)} ${theme.spacing(40.5)} ${theme.spacing(
                      8.5
                    )};
                  align-items: center;
                  background-color: ${theme.colors.neutral5};
                  padding: 10px 0;
                `}
              >
                <BreakRow
                  hasConflict={hasConflict}
                  name={breakItem.breakName}
                  timeRange={[breakItem.startTime, breakItem.endTime]}
                  // Filter out the break names that are already used in the same day (unless it's the current one since
                  // we still want to show it as the selected option in the dropdown)
                  breakNameOptions={availableBreakNames.filter(
                    (name) =>
                      name === breakItem.breakName ||
                      !(breakSchedulesByDay[day] ?? []).some((item) => item.breakName === name)
                  )}
                  existingBreakNames={availableBreakNames}
                  routingActionsEnabled={routingActionsEnabled}
                  day={day}
                  id={breakItem.id}
                  onBreakTimeChange={onBreakTimeChange}
                  onBreakChange={onBreakChange}
                  onBreakRemove={onBreakRemove}
                  onEditBreak={onEditBreak}
                />
              </div>
            );
          })}
        </>
      ))}
    </div>
  );
};

const BreakRow = ({
  name,
  breakNameOptions,
  existingBreakNames,
  timeRange,
  day,
  id,
  routingActionsEnabled,
  onBreakTimeChange,
  onBreakChange,
  onBreakRemove,
  onEditBreak,
  hasConflict,
}: {
  name: string;
  timeRange: [string, string];
  breakNameOptions: string[];
  existingBreakNames: string[];
  day: DaysOfWeek;
  id: string;
  routingActionsEnabled: boolean;
  onBreakTimeChange: (props: { day: DaysOfWeek; startTime: string; endTime: string; id: string }) => void;
  onBreakChange: (props: { day: DaysOfWeek; breakName: string; id: string; isNew?: boolean }) => void;
  onBreakRemove: (props: { day: DaysOfWeek; id: string }) => void;
  onEditBreak: (id: string, newName: string) => void;
  hasConflict: boolean;
}) => {
  const { t } = useTranslation('phone');

  const {
    modalProps: editBreakModalProps,
    closeModal: editBreakCloseModal,
    openModal: editBreakOpenModal,
  } = useModalControl();
  const {
    modalProps: newBreakModalProps,
    closeModal: newBreakCloseModal,
    openModal: newBreakOpenModal,
  } = useModalControl();

  const nameField = useControlledField({
    type: 'dropdown',
    required: true,
    onChange: (value: string) => {
      if (value === '') {
        return;
      }
      onBreakChange({ day, breakName: value, id });
    },
    value: name,
  });

  const timeRangeField = useControlledField({
    type: 'timeRange',
    required: true,
    interval: 60,
    onChange: (value: [string, string]) => {
      onBreakTimeChange({ day, startTime: value[0], endTime: value[1], id });
    },
    value: timeRange,
  });

  const timeFieldCompleted = timeRangeField.displayValue.every((time) => time.endsWith('am') || time.endsWith('pm'));
  const showConflictError = hasConflict && timeFieldCompleted;

  // Reset timeRangeField when timeRange changes (e.g., after onBreakRemove)
  useEffect(() => {
    timeRangeField.onChange?.({ name: 'breakTimeRange', value: timeRange });
  }, [name]);

  const { getMenuProps, getTriggerProps, close: closePopupMenu } = usePopoverMenu();

  // Auto-select the break if there is only one available
  useEffect(() => {
    if (breakNameOptions.length === 1 && breakNameOptions[0] !== name) {
      onBreakChange({ day, breakName: breakNameOptions[0], id });
    }
  }, [breakNameOptions, name, day, id, onBreakChange]);

  return (
    <>
      <div />
      <div>
        <DropdownField
          hideErrorText
          {...nameField}
          name='breakName'
          label='Select Break'
          css={css`
            width: 200px;
            background-color: ${theme.colors.white};
            ${!!nameField.value && showConflictError ? `margin-bottom: ${theme.spacing(3.25)};` : ''}
          `}
        >
          <DropdownField.Option
            css={css`
              padding: 0;
            `}
            key={'new'}
            disabled={!routingActionsEnabled}
            value={''}
            isSelectable={false}
          >
            <TooltipWrapper
              tooltipDisabled={routingActionsEnabled}
              tooltipText={t('New breaks are created in the Call Route experience.')}
            >
              <Button
                iconName='plus'
                variant='tertiary'
                disabled={!routingActionsEnabled}
                onClick={() => newBreakOpenModal()}
                css={css`
                  width: 100%;
                  justify-content: start;
                `}
              >
                {t('New Break')}
              </Button>
            </TooltipWrapper>
          </DropdownField.Option>
          {breakNameOptions.map((breakName) => (
            <DropdownField.Option key={breakName} value={breakName}>
              {breakName}
            </DropdownField.Option>
          ))}
        </DropdownField>

        {!nameField.value && (
          <Text
            color='error'
            size='small'
            css={css`
              padding-top: ${theme.spacing(1)};
            `}
          >
            {t('This field is required.')}
          </Text>
        )}
      </div>

      <div>
        <TimeRangeField
          {...timeRangeField}
          name='breakTimeRange'
          label='Break Time Range'
          interval={15}
          hasError={hasConflict}
          hideErrorText={true}
          joiningText='to'
          css={css`
            ${(!nameField.value && !hasConflict) || (!nameField.value && !timeFieldCompleted)
              ? `padding-bottom: ${theme.spacing(3)};`
              : ''}
            input {
              background-color: ${theme.colors.white} !important;
              border-radius: ${theme.spacing(0.5)};
            }
          `}
        />
        {showConflictError && (
          <Text
            color='error'
            size='small'
            css={css`
              width: 500px;
              padding-top: ${theme.spacing(1)};
            `}
          >
            {t('This break overlaps with another break.')}
          </Text>
        )}
      </div>
      {!name ? (
        <Button
          iconName='remove-small'
          variant='secondary'
          destructive
          css={css`
            width: 40px;
            ${!nameField.value || showConflictError ? `margin-bottom: ${theme.spacing(3)};` : ''}
          `}
          onClick={() => {
            onBreakRemove({ day, id });
          }}
        />
      ) : (
        <>
          <Button
            iconName='more-small'
            variant='secondary'
            {...getTriggerProps()}
            css={css`
              width: 40px;
              ${!nameField.value || showConflictError ? `margin-bottom: ${theme.spacing(3)};` : ''}
            `}
          />

          <PopoverMenu {...getMenuProps()}>
            <PopoverMenuItem
              css={css`
                cursor: pointer;
              `}
              Icon={() => <Icon name='edit-small' />}
              onClick={() => {
                editBreakOpenModal();
                closePopupMenu();
              }}
            >
              <Text as='span'> {t('Rename Break')}</Text>
            </PopoverMenuItem>
            <PopoverMenuItem
              css={css`
                cursor: pointer;
              `}
              Icon={() => <Icon name='remove-small' />}
              onClick={() => {
                onBreakRemove({ day, id });
                closePopupMenu();
              }}
            >
              <Text as='span'> {t('Remove Break')}</Text>
            </PopoverMenuItem>
          </PopoverMenu>
        </>
      )}

      <Modal {...editBreakModalProps} minWidth={400}>
        <EditBreakModal
          name={name}
          // Filter out the current name from the existing names to allow renaming to the same name
          existingBreakNames={existingBreakNames.filter((existingName) => existingName !== name)}
          onCancel={editBreakCloseModal}
          onDone={(newName) => {
            editBreakCloseModal();
            if (newName !== name) {
              onEditBreak(id, newName);
            }
          }}
        />
      </Modal>

      <Modal {...newBreakModalProps} minWidth={400}>
        <NewBreakModal
          onCancel={newBreakCloseModal}
          // Filter out the current name from the existing names to allow renaming to the same name
          existingBreakNames={existingBreakNames.filter((existingName) => existingName !== name)}
          onDone={(newName) => {
            onBreakChange({ day, breakName: newName, id, isNew: true });
            newBreakCloseModal();
          }}
        />
      </Modal>
    </>
  );
};
